import React, { useEffect, useState, createContext, useContext } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, User, GoogleAuthProvider, signInWithPopup, browserPopupRedirectResolver } from 'firebase/auth';
import { auth, db } from '../utils/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signup: (email: string, password: string, name: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  userProfile: UserProfile | null;
  authError: string | null;
}
interface UserProfile {
  name: string;
  role: string;
  bio?: string;
  avatarUrl?: string;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  async function signup(email: string, password: string, name: string) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Create user profile in Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        name,
        email,
        role: 'reporter',
        createdAt: new Date().toISOString()
      });
      setAuthError(null);
    } catch (error: any) {
      console.error('Error signing up:', error);
      setAuthError(error.message || 'Failed to sign up');
      throw error;
    }
  }
  function login(email: string, password: string) {
    setAuthError(null);
    return signInWithEmailAndPassword(auth, email, password).catch(error => {
      setAuthError(error.message || 'Failed to log in');
      throw error;
    });
  }
  async function loginWithGoogle() {
    try {
      console.log('Starting Google sign-in process...');
      setAuthError(null);
      // Create a new provider instance each time
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      console.log('Opening Google sign-in popup...');
      // Use the explicit resolver to help with popup issues
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      console.log('Google sign-in successful, getting user data...');
      const user = result.user;
      // At this point, authentication is successful, so we can consider the login successful
      // even if Firestore operations fail
      try {
        // Check if user profile exists
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        // If user doesn't exist in Firestore, create a profile
        if (!docSnap.exists()) {
          console.log('Creating new user profile in Firestore...');
          try {
            await setDoc(docRef, {
              name: user.displayName || 'Google User',
              email: user.email,
              role: 'reporter',
              createdAt: new Date().toISOString(),
              avatarUrl: user.photoURL || undefined
            });
            console.log('User profile created successfully');
          } catch (firestoreError: any) {
            console.error('Error creating user profile:', firestoreError);
            // Don't throw error here, we'll still consider auth successful
            // but log a warning that profile creation failed
            console.warn('User authenticated but profile creation failed. Some features may be limited.');
          }
        } else {
          console.log('Existing user found in Firestore');
        }
      } catch (firestoreError: any) {
        console.error('Error accessing Firestore:', firestoreError);
        if (firestoreError.message && firestoreError.message.includes('offline')) {
          console.warn('Network appears to be offline. User is authenticated but profile data could not be accessed.');
        }
        // Don't throw error here - authentication was successful even if Firestore operations failed
      }
      return result;
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      // Provide more specific error messages based on the error code
      if (error.code === 'auth/unauthorized-domain') {
        setAuthError('This domain is not authorized for Firebase authentication. You need to add this domain to your Firebase project authorized domains list in the Firebase Console under Authentication > Sign-in method > Authorized domains.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setAuthError('The sign-in popup was closed before completing. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setAuthError('The sign-in popup was blocked by your browser. Please allow popups for this site.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setAuthError('Multiple popup requests were made. Please try again.');
      } else if (error.code === 'auth/network-request-failed') {
        setAuthError('Network error. Please check your internet connection and try again.');
      } else {
        setAuthError(error.message || 'Failed to sign in with Google');
      }
      throw error;
    }
  }
  function logout() {
    setAuthError(null);
    return signOut(auth).catch(error => {
      setAuthError(error.message || 'Failed to log out');
      throw error;
    });
  }
  async function fetchUserProfile(user: User) {
    try {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const userData = docSnap.data() as UserProfile;
        setUserProfile(userData);
      } else {
        console.log('No user profile found');
        // If we have a user but no profile, create a basic profile
        try {
          const basicProfile = {
            name: user.displayName || user.email?.split('@')[0] || 'User',
            email: user.email,
            role: 'reporter',
            createdAt: new Date().toISOString(),
            avatarUrl: user.photoURL || undefined
          };
          await setDoc(docRef, basicProfile);
          setUserProfile(basicProfile as UserProfile);
          console.log('Created basic profile for existing user');
        } catch (error) {
          console.error('Failed to create basic profile:', error);
          setUserProfile(null);
        }
      }
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      // If offline, set a basic profile from auth data so the app can still function
      if (error.message && error.message.includes('offline')) {
        console.warn('Network appears to be offline. Using basic profile from auth data.');
        const basicProfile = {
          name: user.displayName || user.email?.split('@')[0] || 'User',
          role: 'reporter',
          avatarUrl: user.photoURL || undefined
        };
        setUserProfile(basicProfile as UserProfile);
      } else {
        setUserProfile(null);
      }
    }
  }
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);
  const value = {
    currentUser,
    userProfile,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    authError
  };
  return <AuthContext.Provider value={value} data-id="element-292">
      {!loading && children}
    </AuthContext.Provider>;
};